import {
  useLayoutEffect,
  useRef,
  type ComponentProps,
  type RefObject,
} from "react";
import { cn } from "@/lib/utils";

/**
 * How much of the window a long list takes on a wide screen. A card that holds its rows with
 * other parts, as Define's figure list and element card do, takes it itself and gives the
 * rows the `card` bound.
 */
export const LIST_BOUND = "lg:max-h-list";

/**
 * How much of the window a list's rows take on a wide screen before they scroll inside: a
 * step's list; a short list for rows inside one card of a list, so the cards around it stay
 * in view; or the room left in a card that takes the list bound itself, under its heading
 * and over its footer.
 */
const BOUND = {
  list: LIST_BOUND,
  short: "lg:max-h-list-short",
  card: "",
} as const;

/**
 * Make the rows a region the keyboard can reach while they run past the box they are held to,
 * and plain rows while they fit. Checked after each render, which is when rows come and go,
 * and on a resize of the box or a row. Set on the element, as Menu sets its side: it is a
 * fact of the layout, not of what the rows say.
 */
function useReachableWhileScrolling(
  ref: RefObject<HTMLDivElement | null>,
  label: string,
): void {
  useLayoutEffect(() => {
    const rows = ref.current;
    if (rows === null) return undefined;
    const check = () => {
      if (rows.scrollHeight > rows.clientHeight) {
        rows.setAttribute("role", "region");
        rows.setAttribute("aria-label", label);
        rows.tabIndex = 0;
      } else {
        rows.removeAttribute("role");
        rows.removeAttribute("aria-label");
        rows.removeAttribute("tabindex");
      }
    };
    check();
    const watch = new ResizeObserver(check);
    watch.observe(rows);
    for (const row of rows.children) watch.observe(row);
    return () => {
      watch.disconnect();
    };
  });
}

/**
 * A long list's rows, held to part of the window on a wide screen and scrolled inside, so the
 * list ends above what comes after it and its heading and footer stay in view.
 *
 * It holds the rows only: the card around them keeps its border, its heading and its footer.
 * Below the wide breakpoint the page is one column and scrolls as a whole, so the rows grow
 * with it rather than putting a scroll inside a scroll. A menu opened from a row fits itself
 * inside this box, as the box clips it.
 *
 * While the rows run past the bound it is a region named by `label` that the keyboard can
 * reach and scroll, ringed when focused: rows of words hold nothing focusable, and a browser
 * that does not focus a scrolling box itself would leave the rows past the bound out of reach.
 */
export function ScrollRows({
  label,
  bound = "list",
  className,
  ...props
}: Omit<ComponentProps<"div">, "ref" | "role" | "tabIndex"> & {
  label: string;
  bound?: keyof typeof BOUND;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useReachableWhileScrolling(ref, label);
  return (
    <div
      {...props}
      ref={ref}
      className={cn(
        "rounded-sm focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:outline-none focus-visible:ring-inset lg:overflow-y-auto lg:overscroll-contain",
        BOUND[bound],
        className,
      )}
    />
  );
}
